// src/components/ProcessingResults/ReprocessJobModal.jsx
//
// Shared "reprocess with Triton" parameters modal — same fields as the
// Processing Parameters modal in PhotoProcessingPage.js (Confidence/IoU
// thresholds, Plate Detection switch, YOLO Model Type), reused for two
// entry points:
//   - mode="single": ProcessingResultsPage.js, one job. Every field is
//     pre-filled with that job's own previous run (job.inference_parameters).
//   - mode="batch": JobsPage.js Action 1, N selected jobs. Confidence/IoU/
//     Plate Detection start from the same defaults as a brand-new upload
//     (PhotoProcessingPage.js), while the YOLO model type is set per photo,
//     independently, defaulting to whatever model each photo used last time
//     (mirrors the per-file model table on PhotoProcessingPage.js).
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { yoloModelOptions } from '../../constants/yoloModels';
import useImageWithAuth from '../../hooks/useImageWithAuth';
import { withCacheBust } from '../../utils/mediaUtils';

const DEFAULT_PARAMETERS = {
  confidenceThreshold: 0.3,
  iouThreshold: 0.5,
  maxDetections: 96,
  yolo_model_type: 0,
  enable_plate_detection: true
};

// job.inference_parameters is stored by the backend in snake_case (see
// tasks.process_photo_analysis) — map it onto the modal's parameter shape.
const paramsFromInference = (inferenceParameters) => {
  if (!inferenceParameters) return null;
  return {
    confidenceThreshold: inferenceParameters.confidence_threshold ?? DEFAULT_PARAMETERS.confidenceThreshold,
    iouThreshold: inferenceParameters.iou_threshold ?? DEFAULT_PARAMETERS.iouThreshold,
    maxDetections: inferenceParameters.max_detections ?? DEFAULT_PARAMETERS.maxDetections,
    yolo_model_type: inferenceParameters.yolo_model_type ?? DEFAULT_PARAMETERS.yolo_model_type,
    enable_plate_detection: inferenceParameters.enable_plate_detection ?? DEFAULT_PARAMETERS.enable_plate_detection
  };
};

const hasManualAdjustments = (job) => Boolean(
  (job?.adjusted_grid_structure && job.adjusted_grid_structure.length > 0) ||
  (job?.extracted_colors && job.extracted_colors.length > 0)
);

const JobThumbnailRow = ({ job, modelType, onModelChange }) => {
  const thumbnailUrl = withCacheBust(job.rotated_thumbnail || job.thumbnail, job.user_modifications_saved_at);
  const [image, imageStatus] = useImageWithAuth('photo_processing', thumbnailUrl);

  return (
    <div className="mb-3">
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          {imageStatus === 'loaded' && image ? (
            <img
              src={image.src}
              alt={job.original_image_filename || `Job #${job.id}`}
              style={{ width: '30px', height: '30px', objectFit: 'cover', marginRight: '10px' }}
            />
          ) : (
            <div
              className="bg-light border rounded"
              style={{ width: '30px', height: '30px', marginRight: '10px' }}
            />
          )}
          <span className="small">{job.original_image_filename || `Job #${job.id}`}</span>
        </div>
        <Form.Select
          size="sm"
          value={modelType}
          onChange={(e) => onModelChange(job.id, parseInt(e.target.value, 10))}
          style={{ width: '150px' }}
        >
          {yoloModelOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      </div>
    </div>
  );
};

/**
 * @param {boolean} show
 * @param {() => void} onHide
 * @param {'single'|'batch'} mode
 * @param {Array<Object>} jobs - jobs being reprocessed (length 1 for 'single')
 * @param {(processingParams: Object, yoloModelTypesByJobId?: Object) => void} onConfirm
 * @param {boolean} submitting
 */
const ReprocessJobModal = ({ show, onHide, mode, jobs = [], onConfirm, submitting = false }) => {
  const isBatch = mode === 'batch';

  const [parameters, setParameters] = useState(DEFAULT_PARAMETERS);
  const [perJobModelTypes, setPerJobModelTypes] = useState({});
  const [hasPreviousParams, setHasPreviousParams] = useState(false);
  const [warnAboutAdjustments, setWarnAboutAdjustments] = useState(false);

  // (Re)initialize every time the modal opens for a (possibly new) job set,
  // rather than on every jobs/mode change, so edits made while it's open
  // aren't clobbered by upstream re-renders.
  useEffect(() => {
    if (!show) return;

    if (isBatch) {
      setParameters(DEFAULT_PARAMETERS);
      setHasPreviousParams(false);

      const initialModels = {};
      jobs.forEach(job => {
        initialModels[job.id] = job.inference_parameters?.yolo_model_type ?? 0;
      });
      setPerJobModelTypes(initialModels);
    } else {
      const job = jobs[0];
      const previous = paramsFromInference(job?.inference_parameters);
      setParameters(previous || DEFAULT_PARAMETERS);
      setHasPreviousParams(Boolean(previous));
      setPerJobModelTypes({});
    }

    setWarnAboutAdjustments(jobs.some(hasManualAdjustments));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const handleParameterChange = (param, value) => {
    setParameters(prev => ({ ...prev, [param]: value }));
  };

  const handleModelChange = (jobId, value) => {
    setPerJobModelTypes(prev => ({ ...prev, [jobId]: value }));
  };

  const handleApplyGlobalModel = () => {
    const updated = {};
    jobs.forEach(job => { updated[job.id] = parameters.yolo_model_type; });
    setPerJobModelTypes(updated);
  };

  const handleConfirm = () => {
    const processingParams = {
      confidence_threshold: parameters.confidenceThreshold,
      iou_threshold: parameters.iouThreshold,
      max_detections: parameters.maxDetections,
      enable_plate_detection: parameters.enable_plate_detection,
      yolo_model_type: parameters.yolo_model_type
    };

    if (isBatch) {
      onConfirm(processingParams, perJobModelTypes);
    } else {
      onConfirm(processingParams);
    }
  };

  const title = isBatch
    ? `Reprocess ${jobs.length} Photo${jobs.length !== 1 ? 's' : ''}`
    : 'Reprocess Photo';

  return (
    <Modal show={show} onHide={submitting ? undefined : onHide} size="lg" centered>
      <Modal.Header closeButton={!submitting}>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!isBatch && (
          <Alert variant="info" className="small mb-3">
            {hasPreviousParams
              ? "The values below are the parameters used in this job's last Triton run — adjust anything you'd like to change before reprocessing."
              : 'This job has no previous successful run to copy parameters from — showing default parameters.'}
          </Alert>
        )}
        {isBatch && (
          <Alert variant="info" className="small mb-3">
            Confidence Threshold, IoU Threshold and Plate Detection apply to all
            selected photos. Each photo's Model Type below defaults independently
            to whatever it used in its own last run — change it per photo, or
            pick a model above and click "Apply to All".
          </Alert>
        )}
        {warnAboutAdjustments && (
          <Alert variant="warning" className="small mb-3">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {isBatch
              ? 'Some of the selected photos have manually saved grid/color adjustments. Reprocessing will discard them and recompute detections from scratch.'
              : 'This photo has manually saved grid/color adjustments. Reprocessing will discard them and recompute detections from scratch.'}
          </Alert>
        )}

        <Form>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Confidence Threshold
                  <span className="text-muted ms-2">({parameters.confidenceThreshold})</span>
                </Form.Label>
                <Form.Range
                  min={0.1}
                  max={1.0}
                  step={0.05}
                  value={parameters.confidenceThreshold}
                  onChange={(e) => handleParameterChange('confidenceThreshold', parseFloat(e.target.value))}
                />
                <Form.Text className="text-muted">
                  Minimum confidence score for detections (0.1 - 1.0)
                </Form.Text>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  IoU Threshold
                  <span className="text-muted ms-2">({parameters.iouThreshold})</span>
                </Form.Label>
                <Form.Range
                  min={0.1}
                  max={1.0}
                  step={0.05}
                  value={parameters.iouThreshold}
                  onChange={(e) => handleParameterChange('iouThreshold', parseFloat(e.target.value))}
                />
                <Form.Text className="text-muted">
                  Intersection over Union threshold for NMS (0.1 - 1.0)
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Plate Detection</Form.Label>
                <div>
                  <Form.Check
                    type="switch"
                    id="reprocess-enable-plate-detection"
                    label={parameters.enable_plate_detection ? 'Enabled' : 'Disabled'}
                    checked={parameters.enable_plate_detection}
                    onChange={(e) => handleParameterChange('enable_plate_detection', e.target.checked)}
                  />
                  <Form.Text className="text-muted">
                    When enabled, the system will detect and crop the plate region before cell detection.
                    Disable to process the entire image without plate detection.
                  </Form.Text>
                </div>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>{isBatch ? 'Global Model Type' : 'Model Type'}</Form.Label>
                <div className="d-flex align-items-center mb-2">
                  <Form.Select
                    value={parameters.yolo_model_type}
                    onChange={(e) => handleParameterChange('yolo_model_type', parseInt(e.target.value, 10))}
                    className="me-2"
                  >
                    {yoloModelOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                  {isBatch && (
                    <Button variant="outline-primary" size="sm" onClick={handleApplyGlobalModel}>
                      Apply to All
                    </Button>
                  )}
                </div>
                <Form.Text className="text-muted">
                  {isBatch
                    ? 'Used as the starting point for every photo below; "Apply to All" overwrites each photo\'s individual choice.'
                    : 'Model used for cell detection on this photo.'}
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {isBatch && jobs.length > 0 && (
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Per-photo Model Configuration ({jobs.length} photos)</Form.Label>
                  <div className="per-file-models border rounded p-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {jobs.map(job => (
                      <JobThumbnailRow
                        key={job.id}
                        job={job}
                        modelType={perJobModelTypes[job.id] ?? 0}
                        onModelChange={handleModelChange}
                      />
                    ))}
                  </div>
                  <Form.Text className="text-muted">
                    Each photo defaults to the model it used in its own previous run.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="warning" onClick={handleConfirm} disabled={submitting || jobs.length === 0}>
          {submitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Reprocessing...
            </>
          ) : (
            <>
              <i className="bi bi-arrow-repeat me-2"></i>
              Reprocess
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ReprocessJobModal;
